import React, { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { Sheet } from '../types';
import {
  getSortedLayers,
  layerDisplayName,
  resolveActiveLayerId,
  shouldHideLayerChipWhenIdle,
} from '../logic/LayerLogic';
import { GuideContext } from '../contexts/GuideContext';
import { useLocale } from '../contexts/LocaleContext';
import { handleLayerRemoveButtonClick, clearLayerRemoveConfirming } from '../utils/layerRemoveButton';
import {
  formatLayerActiveChipGuideHtml,
  formatLayerAddButtonGuideHtml,
  formatLayerInactiveChipGuideHtml,
  formatLayerListExtendedGuideHtml,
  formatLayerNameEditingGuideHtml,
  formatLayerRemoveButtonGuideHtml,
} from '../utils/tooltipFormatter';
import './styles/SheetLayerCalcTitleList.scss';

const LAYER_LIST_GUIDE_HOVER_MS = 2000;
/** .sheet-layer-calc-item-input の max-width（14em） */
const LAYER_EDIT_LABEL_MAX_EM = 14;

const measureLayerEditInputWidth = (
  input: HTMLInputElement,
  text: string,
  baseWidth: number | null,
  measureSpan: HTMLSpanElement | null,
): number | null => {
  const styles = getComputedStyle(input);
  const span = measureSpan ?? document.createElement('span');
  if (!measureSpan) {
    span.setAttribute('aria-hidden', 'true');
    span.style.cssText =
      'position:fixed;left:-9999px;top:0;visibility:hidden;white-space:pre;pointer-events:none;';
    document.body.appendChild(span);
  }
  span.style.font = styles.font;
  span.textContent = text || input.placeholder || ' ';
  const textWidth = span.getBoundingClientRect().width;
  if (!measureSpan) {
    document.body.removeChild(span);
  }
  const paddingX =
    (parseFloat(styles.paddingLeft) || 0) + (parseFloat(styles.paddingRight) || 0);
  const borderX =
    (parseFloat(styles.borderLeftWidth) || 0) + (parseFloat(styles.borderRightWidth) || 0);
  const contentWidth = Math.ceil(textWidth + paddingX + borderX);
  const fontSize = parseFloat(styles.fontSize) || 12;
  const maxWidth = Math.ceil(fontSize * LAYER_EDIT_LABEL_MAX_EM);
  const floor = baseWidth ?? 0;
  return Math.min(maxWidth, Math.max(floor, contentWidth));
};

interface SheetLayerCalcTitleListProps {
  sheet: Sheet;
  onSelectLayer: (layerId: string) => void;
  onAddLayer: () => void;
  onRenameLayer?: (layerId: string, name: string) => void;
  onRemoveLayer?: (layerId: string) => void;
  /** App から Alt++ 等で追加したあと名前編集へフォーカス */
  pendingFocusEditRef?: React.MutableRefObject<boolean>;
}

const layerNameInputStaticHandlers = (): Pick<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onMouseDown' | 'onTouchEnd' | 'onCompositionStart' | 'onCompositionEnd'
> => ({
  onMouseDown: e => {
    const target = e.currentTarget;
    const wasFocused = document.activeElement === target;
    if (!wasFocused) {
      requestAnimationFrame(() => {
        target.select();
      });
    }
  },
  onTouchEnd: e => {
    e.preventDefault();
    e.stopPropagation();
    const target = e.currentTarget;
    target.focus();
    target.select();
  },
  onCompositionStart: () => {
    (window as Window & { isIMEComposing?: boolean }).isIMEComposing = true;
  },
  onCompositionEnd: () => {
    (window as Window & { isIMEComposing?: boolean }).isIMEComposing = false;
  },
});

/** 計算エリアのシートタイトル直下に表示するレイヤー名一覧（+ で追加。1層でも表示） */
export const SheetLayerCalcTitleList: React.FC<SheetLayerCalcTitleListProps> = ({
  sheet,
  onSelectLayer,
  onAddLayer,
  onRenameLayer,
  onRemoveLayer,
  pendingFocusEditRef: pendingFocusEditRefProp,
}) => {
  const isGuideEnabled = useContext(GuideContext);
  const { appCopy } = useLocale();
  const layers = getSortedLayers(sheet);
  const activeId = resolveActiveLayerId(sheet);
  const hideChipWhenIdle = shouldHideLayerChipWhenIdle(sheet);
  const canRemove = layers.length > 1 && !!onRemoveLayer;
  const canRename = !!onRenameLayer;
  const pendingFocusEditLocalRef = useRef(false);
  const pendingFocusEditRef = pendingFocusEditRefProp ?? pendingFocusEditLocalRef;
  const activeInputRef = useRef<HTMLInputElement>(null);
  const activeChipButtonRef = useRef<HTMLButtonElement>(null);
  const measureSpanRef = useRef<HTMLSpanElement | null>(null);
  /** 編集開始時のチップ幅（iOS 初回膨張防止の下限） */
  const editingChipBaseWidthRef = useRef<number | null>(null);
  const listGuideTimerRef = useRef<number | null>(null);
  const [listGuideTier, setListGuideTier] = useState(0);
  const [isNameInputFocused, setIsNameInputFocused] = useState(false);
  /** アクティブレイヤー名の編集モード（選択と編集を分離） */
  const [editingLayerId, setEditingLayerId] = useState<string | null>(null);
  /** 編集中 input の幅（--layer-edit-label-width） */
  const [editingChipWidth, setEditingChipWidth] = useState<number | null>(null);

  const measureEditingChipWidth = () => {
    const button = activeChipButtonRef.current;
    if (!button) return null;
    const width = button.getBoundingClientRect().width;
    return width > 0 ? Math.ceil(width) : null;
  };

  const syncEditingChipWidth = (input: HTMLInputElement, text: string) => {
    const next = measureLayerEditInputWidth(
      input,
      text,
      editingChipBaseWidthRef.current,
      measureSpanRef.current,
    );
    if (next != null) {
      setEditingChipWidth(next);
    }
  };

  const setEditingChipWidthFromBase = (base: number | null) => {
    editingChipBaseWidthRef.current = base;
    setEditingChipWidth(base);
  };

  const focusActiveLayerInput = () => {
    requestAnimationFrame(() => {
      const el = activeInputRef.current;
      if (el) {
        el.focus();
        el.select();
        el.scrollLeft = 0;
      }
    });
  };

  const handleAddLayerClick = () => {
    pendingFocusEditRef.current = true;
    onAddLayer();
  };

  useEffect(() => {
    clearLayerRemoveConfirming();
  }, [activeId]);

  useEffect(() => {
    if (pendingFocusEditRef.current) {
      pendingFocusEditRef.current = false;
      setEditingChipWidthFromBase(measureEditingChipWidth());
      setEditingLayerId(activeId);
      focusActiveLayerInput();
      return;
    }
    setEditingLayerId(null);
    editingChipBaseWidthRef.current = null;
    setEditingChipWidth(null);
  }, [activeId, layers.length]);

  useLayoutEffect(() => {
    if (editingLayerId !== activeId) return;
    focusActiveLayerInput();
  }, [editingLayerId, activeId]);

  useLayoutEffect(() => {
    if (editingLayerId !== activeId) return;
    const input = activeInputRef.current;
    const layer = layers.find(l => l.id === editingLayerId);
    if (!input || !layer) return;
    syncEditingChipWidth(input, layer.name ?? '');
  }, [editingLayerId, activeId, layers]);

  useEffect(() => {
    const span = document.createElement('span');
    span.setAttribute('aria-hidden', 'true');
    span.style.cssText =
      'position:fixed;left:-9999px;top:0;visibility:hidden;white-space:pre;pointer-events:none;';
    document.body.appendChild(span);
    measureSpanRef.current = span;
    return () => {
      span.remove();
      measureSpanRef.current = null;
    };
  }, []);

  const editingNameAtStartRef = useRef('');

  const beginLayerNameEdit = (layerId: string) => {
    const layer = layers.find(l => l.id === layerId);
    editingNameAtStartRef.current = layer?.name ?? '';
    setEditingChipWidthFromBase(measureEditingChipWidth());
    setEditingLayerId(layerId);
  };

  const handleLayerChipActivate = (layerId: string) => {
    if (layerId === activeId && canRename) {
      beginLayerNameEdit(layerId);
      return;
    }
    setEditingLayerId(null);
    editingChipBaseWidthRef.current = null;
    setEditingChipWidth(null);
    clearLayerRemoveConfirming();
    if (layerId !== activeId) {
      onSelectLayer(layerId);
    }
  };

  /** iOS: preventDefault すると同一タッチ内の focus が失敗するため、編集開始時は flushSync + 同期 focus */
  const handleLayerChipTouchEnd = (e: React.TouchEvent, layerId: string) => {
    if ((e.target as Element).closest('.layer-remove-btn')) return;
    e.stopPropagation();
    if (layerId === activeId && canRename) {
      flushSync(() => {
        beginLayerNameEdit(layerId);
      });
      const el = activeInputRef.current;
      if (el) {
        el.focus();
        el.select();
      }
      return;
    }
    e.preventDefault();
    handleLayerChipActivate(layerId);
  };

  useEffect(() => {
    return () => {
      if (listGuideTimerRef.current != null) {
        window.clearTimeout(listGuideTimerRef.current);
      }
    };
  }, []);

  const clearListGuideTimer = () => {
    if (listGuideTimerRef.current != null) {
      window.clearTimeout(listGuideTimerRef.current);
      listGuideTimerRef.current = null;
    }
  };

  const handleListMouseEnter = () => {
    if (!isGuideEnabled) return;
    clearListGuideTimer();
    listGuideTimerRef.current = window.setTimeout(() => {
      setListGuideTier(2);
    }, LAYER_LIST_GUIDE_HOVER_MS);
  };

  const handleListMouseLeave = () => {
    clearListGuideTimer();
    setListGuideTier(0);
  };

  const listTooltipHtml = useMemo(() => {
    if (!isGuideEnabled) return undefined;
    const base = [formatLayerInactiveChipGuideHtml(), formatLayerAddButtonGuideHtml()]
      .filter(Boolean)
      .join('<br/>');
    if (listGuideTier < 2) return base || undefined;
    const ext = formatLayerListExtendedGuideHtml(layers.length > 1);
    return ext ? `${base}<br/>${ext}` : base || undefined;
  }, [isGuideEnabled, listGuideTier, layers.length]);

  const activeInputTooltipHtml = useMemo(() => {
    if (!isGuideEnabled) return undefined;
    if (isNameInputFocused) {
      return formatLayerNameEditingGuideHtml(layers.length > 1) || undefined;
    }
    return formatLayerActiveChipGuideHtml() || undefined;
  }, [isGuideEnabled, isNameInputFocused, layers.length]);

  const handleLayerNameChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    layerId: string,
  ) => {
    const value = e.target.value;
    onRenameLayer?.(layerId, value);
    syncEditingChipWidth(e.target, value);
  };

  const handleLayerInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if ((window as Window & { isIMEComposing?: boolean }).isIMEComposing) {
      return;
    }
    if (e.key === 'Tab' && layers.length > 1) {
      e.preventDefault();
      const idx = layers.findIndex(l => l.id === activeId);
      if (idx < 0) return;
      const nextIdx = e.shiftKey
        ? (idx - 1 + layers.length) % layers.length
        : (idx + 1) % layers.length;
      if (nextIdx === idx) return;
      pendingFocusEditRef.current = true;
      onSelectLayer(layers[nextIdx].id);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      e.currentTarget.blur();
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      const layerId = activeId;
      if (layerId && onRenameLayer) {
        onRenameLayer(layerId, editingNameAtStartRef.current);
      }
      setEditingLayerId(null);
      editingChipBaseWidthRef.current = null;
      setEditingChipWidth(null);
      e.currentTarget.blur();
    }
  };

  const editingChipStyle =
    editingChipWidth != null
      ? ({ '--layer-edit-label-width': `${editingChipWidth}px` } as React.CSSProperties)
      : undefined;

  return (
    <div
      className={`sheet-layer-calc-list${hideChipWhenIdle ? ' is-single-default-layer' : ''}`}
      role="tablist"
      aria-label={appCopy.layer.calcAria}
      data-tooltip-id="tt"
      data-tooltip-html={listTooltipHtml}
      data-tooltip-float={true}
      onMouseEnter={handleListMouseEnter}
      onMouseLeave={handleListMouseLeave}
    >
      <div className="sheet-layer-calc-items">
        {layers.map(layer => {
          const label = layerDisplayName(layer);
          const isActive = layer.id === activeId;
          const isEditing = isActive && canRename && editingLayerId === layer.id;
          return (
            <div
              key={layer.id}
              className={`sheet-layer-calc-chip${isActive ? ' is-active' : ''}${isEditing ? ' is-editing' : ''}${canRemove && isActive && !isEditing ? ' layer-removable' : ''}`}
              style={isEditing ? editingChipStyle : undefined}
            >
              {isEditing ? (
                <input
                  ref={activeInputRef}
                  type="text"
                  role="tab"
                  aria-selected
                  className="sheet-layer-calc-item sheet-layer-calc-item-input"
                  value={layer.name ?? ''}
                  placeholder={appCopy.layer.defaultName}
                  data-tooltip-id="tt"
                  data-tooltip-html={activeInputTooltipHtml}
                  onChange={e => handleLayerNameChange(e, layer.id)}
                  onFocus={() => setIsNameInputFocused(true)}
                  onBlur={() => {
                    setIsNameInputFocused(false);
                    setEditingLayerId(null);
                    editingChipBaseWidthRef.current = null;
                    setEditingChipWidth(null);
                  }}
                  onCompositionEnd={e => {
                    (window as Window & { isIMEComposing?: boolean }).isIMEComposing = false;
                    syncEditingChipWidth(e.currentTarget, e.currentTarget.value);
                  }}
                  onClick={e => e.stopPropagation()}
                  onKeyDown={handleLayerInputKeyDown}
                  {...layerNameInputStaticHandlers()}
                />
              ) : (
                <button
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  ref={isActive ? activeChipButtonRef : undefined}
                  className="sheet-layer-calc-item"
                  data-tooltip-id="tt"
                  data-tooltip-html={
                    isActive
                      ? activeInputTooltipHtml
                      : formatLayerInactiveChipGuideHtml() || undefined
                  }
                  onClick={e => {
                    e.stopPropagation();
                    handleLayerChipActivate(layer.id);
                  }}
                  onTouchEnd={e => handleLayerChipTouchEnd(e, layer.id)}
                >
                  {label}
                </button>
              )}
              {canRemove ? (
                <button
                  type="button"
                  className="layer-remove-btn"
                  data-layer-id={layer.id}
                  data-tooltip-id="tt"
                  data-tooltip-html={
                    isActive && !isEditing
                      ? formatLayerRemoveButtonGuideHtml() || undefined
                      : undefined
                  }
                  aria-label={appCopy.layer.removeAria(label)}
                  aria-hidden={!isActive || isEditing}
                  tabIndex={isActive && !isEditing ? undefined : -1}
                  onClick={e => {
                    if (!isActive || isEditing) return;
                    handleLayerRemoveButtonClick(e, layer.id, onRemoveLayer);
                  }}
                  onTouchEnd={e => {
                    if (!isActive || isEditing) return;
                    handleLayerRemoveButtonClick(e, layer.id, onRemoveLayer);
                  }}
                >
                  −
                </button>
              ) : null}
            </div>
          );
        })}
        <button
          type="button"
          className="layer-add-btn"
          title={appCopy.layer.addAria}
          aria-label={appCopy.layer.addAria}
          data-tooltip-id="tt"
          data-tooltip-html={formatLayerAddButtonGuideHtml() || undefined}
          onClick={e => {
            e.stopPropagation();
            handleAddLayerClick();
          }}
          onTouchEnd={e => {
            e.stopPropagation();
            e.preventDefault();
            handleAddLayerClick();
          }}
        >
          +
        </button>
      </div>
    </div>
  );
};
